import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from './context/AuthContext';
import { AUTH_LOGOUT_EVENT } from './services/api';

// Layout
import Layout from './components/Layout/Layout';

// Pages
import Login from './pages/Login';
import SsoLogin from './pages/SsoLogin';
import SsoSuccess from './pages/SsoSuccess';
import Dashboard from './pages/Dashboard';
import CustomerList from './pages/Customers/CustomerList';
import CustomerForm from './pages/Customers/CustomerForm';
import CustomerView from './pages/Customers/CustomerView';
import ProductList from './pages/Products/ProductList';
import ProductForm from './pages/Products/ProductForm';
import QuotationList from './pages/Quotations/QuotationList';
import QuotationBuilder from './pages/Quotations/QuotationBuilder';
import QuotationView from './pages/Quotations/QuotationView';
import SalesOrdersList from './pages/SalesOrders/SalesOrdersList';
import Activities from './pages/Activities/Activities';
import Templates from './pages/Templates/Templates';
import ContactList from './pages/Contacts/ContactList';
import ContactForm from './pages/Contacts/ContactForm';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Role-gated Route — requires auth AND a role in `roles`. Non-matching users go to /.
const RoleRoute = ({ roles, children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!roles.includes(user?.role)) return <Navigate to="/" replace />;

  return <Layout>{children}</Layout>;
};

// Public Route Component (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  const navigate = useNavigate();

  // Listen for auth logout events from API interceptor
  useEffect(() => {
    const handleAuthLogout = () => {
      navigate('/login', { replace: true });
    };

    window.addEventListener(AUTH_LOGOUT_EVENT, handleAuthLogout);
    return () => window.removeEventListener(AUTH_LOGOUT_EVENT, handleAuthLogout);
  }, [navigate]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      {/* Registration is removed — accounts are auto-provisioned on first Microsoft sign-in */}
      <Route path="/register" element={<Navigate to="/login" replace />} />

      {/* SSO routes — always accessible; they handle auth themselves */}
      <Route path="/sso" element={<SsoLogin />} />
      <Route path="/sso-success" element={<SsoSuccess />} />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Customer Routes */}
      <Route
        path="/customers"
        element={
          <ProtectedRoute>
            <CustomerList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers/new"
        element={
          <ProtectedRoute>
            <CustomerForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers/:id"
        element={
          <ProtectedRoute>
            <CustomerView />
          </ProtectedRoute>
        }
      />

      {/* Product Routes */}
      <Route
        path="/products"
        element={
          <ProtectedRoute>
            <ProductList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/new"
        element={
          <RoleRoute roles={['admin', 'manager']}>
            <ProductForm />
          </RoleRoute>
        }
      />
      <Route
        path="/products/:id"
        element={
          <ProtectedRoute>
            <ProductForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/:id/edit"
        element={
          <RoleRoute roles={['admin', 'manager']}>
            <ProductForm />
          </RoleRoute>
        }
      />

      {/* Quotation Routes */}
      <Route
        path="/quotations"
        element={
          <ProtectedRoute>
            <QuotationList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quotations/new"
        element={
          <ProtectedRoute>
            <QuotationBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quotations/:id"
        element={
          <ProtectedRoute>
            <QuotationView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quotations/:id/edit"
        element={
          <ProtectedRoute>
            <QuotationBuilder />
          </ProtectedRoute>
        }
      />

      {/* Templates Routes */}
      <Route
        path="/templates"
        element={
          <RoleRoute roles={['admin', 'manager']}>
            <Templates />
          </RoleRoute>
        }
      />

      {/* Sales Order Routes */}
      <Route
        path="/sales-orders"
        element={
          <ProtectedRoute>
            <SalesOrdersList />
          </ProtectedRoute>
        }
      />

      {/* Activities Routes */}
      <Route
        path="/activities"
        element={
          <ProtectedRoute>
            <Activities />
          </ProtectedRoute>
        }
      />

      {/* Contact Routes */}
      <Route
        path="/contacts"
        element={
          <ProtectedRoute>
            <ContactList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts/new"
        element={
          <ProtectedRoute>
            <ContactForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts/:id/edit"
        element={
          <ProtectedRoute>
            <ContactForm />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
